import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { getMapImageUrl } from '../../utils/formatters';
import './ZoneEditorModal.css';

/**
 * Konvertera world coords (x, y) till pixel-position pa radar-bilden.
 * `offset_x`/`offset_y` ar world-koordinaten for radar-bildens overst-vanstra
 * horn. `scale` ar world-units per pixel. Y-axeln inverteras (radar vaxer
 * nedat, world vaxer uppat).
 *
 * @param {{offset_x:number, offset_y:number, scale:number}} cal Kalibrering.
 * @param {number} wx World X-koordinat.
 * @param {number} wy World Y-koordinat.
 * @returns {{px:number, py:number}} Pixel-koordinater.
 */
function worldToPixel(cal, wx, wy) {
  return {
    px: (wx - cal.offset_x) / cal.scale,
    py: (cal.offset_y - wy) / cal.scale,
  };
}

/**
 * Konvertera pixel-position pa radar till world coords. Inverse av worldToPixel.
 *
 * @param {{offset_x:number, offset_y:number, scale:number}} cal Kalibrering.
 * @param {number} px Pixel X.
 * @param {number} py Pixel Y.
 * @returns {{wx:number, wy:number}} World-koordinater.
 */
function pixelToWorld(cal, px, py) {
  return {
    wx: cal.offset_x + px * cal.scale,
    wy: cal.offset_y - py * cal.scale,
  };
}

/**
 * Skapa en initial kalibrering som placerar position-datas bounding box
 * runt mitten av radar-bilden, med ~80% av bildens bredd som basskala.
 * Anvands nar admin oppnar editor for forsta gangen — utgangspunkt foljer
 * datat istallet for slumpmassiga default-varden.
 *
 * @param {Array} positions Sample-positions: array av [x,y,z,...] eller {x,y,...}.
 * @param {{w:number, h:number}} imgSize Radar-bildens nativa pixel-matt.
 * @returns {{offset_x:number, offset_y:number, scale:number}}
 */
function autoCalibration(positions, imgSize) {
  if (!positions || positions.length === 0) {
    return { offset_x: -2500, offset_y: 2500, scale: 5.0 };
  }
  let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
  for (const p of positions) {
    const wx = Array.isArray(p) ? p[0] : p.x;
    const wy = Array.isArray(p) ? p[1] : p.y;
    if (wx < xMin) xMin = wx;
    if (wx > xMax) xMax = wx;
    if (wy < yMin) yMin = wy;
    if (wy > yMax) yMax = wy;
  }
  const dataWidth = Math.max(xMax - xMin, 1);
  const targetPxWidth = imgSize.w * 0.8;
  const scale = dataWidth / targetPxWidth;
  // Placera bounding-box ungefar 10% in fran vanster/topp
  const offset_x = xMin - imgSize.w * 0.1 * scale;
  const offset_y = yMax + imgSize.h * 0.1 * scale;
  return { offset_x, offset_y, scale };
}

/**
 * Modal med tva-stegs UI for att satta heatmap-zoner pa en karta.
 *
 * Steg 1 (kalibrering): admin laddar in en sample-spelares positions, ser
 * dem som prickar overlayade pa radarn, och justerar offset/scale tills
 * prickarna ligger dar de hor hemma (mest kills runt sites/choke points).
 *
 * Steg 2 (zoner): admin klickar och drar pa radarn for att rita namngivna
 * rektanglar. Pixel-koordinater konverteras till world via kalibreringen.
 *
 * @param {{map:Object, samplePlayerSteamId?:string, onClose:Function, onSaved:Function}} props
 *   `map` ar Map-objektet fran admin-listan; `samplePlayerSteamId` ar valfri
 *   override (default = anvandarens egen steam_id om tillganglig);
 *   `onClose`/`onSaved` ar callbacks.
 * @returns {JSX.Element}
 */
export default function ZoneEditorModal({ map, samplePlayerSteamId, onClose, onSaved }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [step, setStep] = useState('calibration'); // 'calibration' | 'zones'
  const [calibration, setCalibration] = useState({ offset_x: 512, offset_y: 512, scale: 1.0 });
  const [zones, setZones] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [autoInfo, setAutoInfo] = useState('');

  // Drag-state for kalibrering
  const dragRef = useRef(null);
  // Pagaende zon-rektangel under ritning (i pixel-coords)
  const [drawingRect, setDrawingRect] = useState(null);
  const [pendingZoneName, setPendingZoneName] = useState('');
  const svgRef = useRef(null);

  // Bilddimensioner (laddas vid img-load)
  const [imgSize, setImgSize] = useState({ w: 1024, h: 1024 });

  const radarUrl = map.radar_url
    ? `${getMapImageUrl(map.radar_url)}?v=${Date.parse(map.updated_at || Date.now())}`
    : null;

  // Initial-load: hamta zoner, kalibrering och en sample-heatmap att overlaya.
  // Om kartan saknar sparad kalibrering, autocomputeas en baseline fran
  // sample-positionerna sa prickarna ar synliga direkt (admin behover bara
  // finjustera istallet for att gissa fran (1,1,1)).
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      let savedCal = null;
      let posList = [];
      try {
        const [zRes, cRes] = await Promise.all([
          fetch(`${API_URL}/api/maps/${map.id}/zones`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/api/maps/${map.id}/calibration`, { headers: getAuthHeaders() }),
        ]);
        if (!cancelled && zRes.ok) {
          const z = await zRes.json();
          setZones(z.zones || []);
        }
        if (!cancelled && cRes.ok) {
          const c = await cRes.json();
          if (c.calibration && c.calibration.scale) {
            savedCal = c.calibration;
          }
        }
        if (samplePlayerSteamId) {
          const pRes = await fetch(
            `${API_URL}/api/stats/player/${samplePlayerSteamId}/heatmap?map=${encodeURIComponent(map.tag)}&type=kill`,
            { headers: getAuthHeaders() },
          );
          if (!cancelled && pRes.ok) {
            const p = await pRes.json();
            posList = p.positions || [];
            setPositions(posList);
          }
        }
      } catch (e) {
        if (!cancelled) setError(t('admin.loadFailedMsg', { msg: e.message }));
      }
      if (!cancelled) {
        if (savedCal) {
          setCalibration(savedCal);
          setStep('zones');
        } else if (posList.length > 0) {
          setCalibration(autoCalibration(posList, imgSize));
        }
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
    // imgSize ar med flit utelamnad — autoCalibration vid img-load triggas
    // via separat effekt nedan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map.id, map.tag, samplePlayerSteamId, getAuthHeaders]);

  // Re-autocompute kalibrering nar bilden faktiskt har laddats med riktiga
  // dimensioner, om vi inte har sparat kal.
  useEffect(() => {
    if (step === 'calibration' && positions.length > 0
        && (!calibration.scale || calibration.scale === 1.0)) {
      setCalibration(autoCalibration(positions, imgSize));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgSize.w, imgSize.h]);

  // Kalibrerings-drag: flytta vyn sa prickarna ligger dar admin vill ha dem.
  // Mus-delta omvandlas fran CSS-pixlar till world-units (via SVG-ratio +
  // current scale). Y inverteras eftersom radar Y vaxer nedat men world Y
  // vaxer uppat.
  const onCalibPointerDown = (e) => {
    if (step !== 'calibration') return;
    const rect = svgRef.current?.getBoundingClientRect();
    const ratio = rect && rect.width ? imgSize.w / rect.width : 1;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startOffsetX: calibration.offset_x,
      startOffsetY: calibration.offset_y,
      ratio,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onCalibPointerMove = (e) => {
    if (!dragRef.current || step !== 'calibration') return;
    const dxCss = e.clientX - dragRef.current.startX;
    const dyCss = e.clientY - dragRef.current.startY;
    const dxNative = dxCss * dragRef.current.ratio;
    const dyNative = dyCss * dragRef.current.ratio;
    const s = calibration.scale;
    setCalibration((c) => ({
      ...c,
      offset_x: dragRef.current.startOffsetX - dxNative * s,
      offset_y: dragRef.current.startOffsetY + dyNative * s,
    }));
  };
  const onCalibPointerUp = (e) => {
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) { /* ok */ }
  };

  const adjustScale = (factor) => {
    setCalibration((c) => ({ ...c, scale: Math.max(0.1, c.scale * factor) }));
  };

  // Konvertera mus-event till SVG-koordinater (pixel-coords i bildens nativa skala)
  const eventToSvgPx = useCallback((evt) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const px = ((evt.clientX - rect.left) / rect.width) * imgSize.w;
    const py = ((evt.clientY - rect.top) / rect.height) * imgSize.h;
    return { px, py };
  }, [imgSize]);

  // Zon-ritning
  const onZoneMouseDown = (e) => {
    if (step !== 'zones') return;
    const p = eventToSvgPx(e);
    if (!p) return;
    setDrawingRect({ startPx: p.px, startPy: p.py, endPx: p.px, endPy: p.py });
  };
  const onZoneMouseMove = (e) => {
    if (!drawingRect) return;
    const p = eventToSvgPx(e);
    if (!p) return;
    setDrawingRect((r) => ({ ...r, endPx: p.px, endPy: p.py }));
  };
  const onZoneMouseUp = () => {
    if (!drawingRect) return;
    const { startPx, startPy, endPx, endPy } = drawingRect;
    if (Math.abs(endPx - startPx) < 5 || Math.abs(endPy - startPy) < 5) {
      setDrawingRect(null);
      return;
    }
    // Konvertera de tva motsatta hornen till world coords
    const a = pixelToWorld(calibration, startPx, startPy);
    const b = pixelToWorld(calibration, endPx, endPy);
    const newZone = {
      name: pendingZoneName || `Zon ${zones.length + 1}`,
      x_min: Math.round(Math.min(a.wx, b.wx)),
      x_max: Math.round(Math.max(a.wx, b.wx)),
      y_min: Math.round(Math.min(a.wy, b.wy)),
      y_max: Math.round(Math.max(a.wy, b.wy)),
    };
    setZones((z) => [...z, newZone]);
    setDrawingRect(null);
    setPendingZoneName('');
  };

  /**
   * Hämtar kartans automatiska zoner (klustrade ur nav-meshen) som utgångspunkt.
   *
   * Ersätter listan i redigeraren — inget sparas förrän admin trycker "Spara zoner", och
   * då blir zonerna manuella (vinner över de automatiska i heatmapen). Rektanglarna är
   * zonernas omslutande rektanglar och kan överlappa; justera vid behov.
   *
   * @returns {Promise<void>}
   */
  const loadAutoZones = async () => {
    if (zones.length && !window.confirm(t('admin.zoneLoadAutoConfirm'))) return;
    setBusy(true);
    setAutoInfo('');
    try {
      const r = await fetch(`${API_URL}/api/maps/${map.id}/auto-zones`, { headers: getAuthHeaders() });
      const data = await r.json();
      if (!r.ok || !data.available) {
        setAutoInfo(t('admin.zoneAutoUnavailable'));
      } else {
        setZones(data.zones);
        setAutoInfo(t('admin.zoneAutoLoaded', { count: data.zones.length }));
      }
    } catch (e) {
      setAutoInfo(t('admin.loadFailedMsg', { msg: e.message }));
    }
    setBusy(false);
  };

  const removeZone = (idx) => {
    setZones((z) => z.filter((_, i) => i !== idx));
  };
  const renameZone = (idx, name) => {
    setZones((z) => z.map((zone, i) => (i === idx ? { ...zone, name } : zone)));
  };

  const saveCalibration = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/maps/${map.id}/calibration`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ calibration }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        setError(b.error || t('admin.errorStatus', { status: r.status }));
      } else {
        setStep('zones');
      }
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  const saveZones = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/maps/${map.id}/zones`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ zones }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        setError(b.error || t('admin.errorStatus', { status: r.status }));
      } else {
        onSaved && onSaved();
        onClose();
      }
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  // Beraknade overlay-element
  const dotsToRender = positions.slice(0, 200).map((p, i) => {
    const wx = Array.isArray(p) ? p[0] : p.x;
    const wy = Array.isArray(p) ? p[1] : p.y;
    const { px, py } = worldToPixel(calibration, wx, wy);
    return <circle key={i} cx={px} cy={py} r={3} className="zone-editor-dot" />;
  });

  const zoneRectsToRender = zones.map((z, i) => {
    const a = worldToPixel(calibration, z.x_min, z.y_max);
    const b = worldToPixel(calibration, z.x_max, z.y_min);
    return (
      <g key={i}>
        <rect
          x={Math.min(a.px, b.px)}
          y={Math.min(a.py, b.py)}
          width={Math.abs(b.px - a.px)}
          height={Math.abs(b.py - a.py)}
          className="zone-editor-rect"
        />
        <text
          x={Math.min(a.px, b.px) + 8}
          y={Math.min(a.py, b.py) + 24}
          className="zone-editor-rect-label"
        >
          {z.name}
        </text>
      </g>
    );
  });

  const drawingPreview = drawingRect ? (
    <rect
      x={Math.min(drawingRect.startPx, drawingRect.endPx)}
      y={Math.min(drawingRect.startPy, drawingRect.endPy)}
      width={Math.abs(drawingRect.endPx - drawingRect.startPx)}
      height={Math.abs(drawingRect.endPy - drawingRect.startPy)}
      className="zone-editor-rect-drawing"
    />
  ) : null;

  if (!radarUrl) {
    return (
      <div className="zone-editor-backdrop" onClick={onClose}>
        <div className="zone-editor-modal" onClick={(e) => e.stopPropagation()}>
          <h2>{t('admin.zoneEditor', { name: map.name })}</h2>
          <div className="zone-editor-error">
            {t('admin.zoneNoRadar')}
          </div>
          <button className="admin-maps-btn" onClick={onClose}>{t('profile.close')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="zone-editor-backdrop" onClick={onClose}>
      <div className="zone-editor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="zone-editor-header">
          <h2>{t('admin.zoneEditorTag', { name: map.name, tag: map.tag })}</h2>
          <button className="zone-editor-close" onClick={onClose}>×</button>
        </div>

        <div className="zone-editor-steps">
          <button
            className={`zone-editor-step ${step === 'calibration' ? 'active' : ''}`}
            onClick={() => setStep('calibration')}
            disabled={busy}
          >
            {t('admin.zoneStep1')}
          </button>
          <button
            className={`zone-editor-step ${step === 'zones' ? 'active' : ''}`}
            onClick={() => setStep('zones')}
            disabled={busy || !calibration.scale}
          >
            {t('admin.zoneStep2')}
          </button>
        </div>

        {loading ? (
          <div className="zone-editor-loading">{t('live.loading')}</div>
        ) : (
          <>
            <div className="zone-editor-instructions">
              {step === 'calibration' ? (
                <>
                  <strong>{t('admin.zoneStep1Label')}</strong> {t('admin.zoneStep1Help')}
                </>
              ) : (
                <>
                  <strong>{t('admin.zoneStep2Label')}</strong> {t('admin.zoneStep2Help')}
                </>
              )}
            </div>

            <div className="zone-editor-canvas-wrap">
              <div
                className="zone-editor-canvas"
                onPointerDown={onCalibPointerDown}
                onPointerMove={onCalibPointerMove}
                onPointerUp={onCalibPointerUp}
                onPointerCancel={onCalibPointerUp}
              >
                <img
                  src={radarUrl}
                  alt={map.name}
                  className="zone-editor-radar"
                  onLoad={(e) => setImgSize({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                  draggable={false}
                />
                <svg
                  ref={svgRef}
                  className="zone-editor-svg"
                  viewBox={`0 0 ${imgSize.w} ${imgSize.h}`}
                  preserveAspectRatio="none"
                  onMouseDown={step === 'zones' ? onZoneMouseDown : undefined}
                  onMouseMove={step === 'zones' ? onZoneMouseMove : undefined}
                  onMouseUp={step === 'zones' ? onZoneMouseUp : undefined}
                  onMouseLeave={step === 'zones' ? onZoneMouseUp : undefined}
                >
                  {step === 'calibration' && dotsToRender}
                  {zoneRectsToRender}
                  {drawingPreview}
                </svg>
              </div>
            </div>

            {step === 'calibration' && (
              <div className="zone-editor-controls">
                <button className="admin-maps-btn" onClick={() => adjustScale(0.9)} disabled={busy}>−</button>
                <span className="zone-editor-scale">scale: {calibration.scale.toFixed(2)}</span>
                <button className="admin-maps-btn" onClick={() => adjustScale(1.1)} disabled={busy}>+</button>
                <span className="zone-editor-cal-info">
                  offset_x={Math.round(calibration.offset_x)},
                  offset_y={Math.round(calibration.offset_y)},
                  {' '}{t('admin.zoneDots', { count: positions.length })}
                </span>
              </div>
            )}

            {step === 'zones' && (
              <div className="zone-editor-zone-form">
                <input
                  type="text"
                  className="zone-editor-name-input"
                  placeholder={t('admin.zoneNamePlaceholder')}
                  value={pendingZoneName}
                  onChange={(e) => setPendingZoneName(e.target.value)}
                />
                <div className="zone-editor-auto">
                  <button
                    type="button"
                    className="admin-maps-btn"
                    onClick={loadAutoZones}
                    disabled={busy}
                  >
                    {t('admin.zoneLoadAuto')}
                  </button>
                  {autoInfo && <span className="zone-editor-auto-info">{autoInfo}</span>}
                </div>
                <div className="zone-editor-zones-list">
                  {zones.length === 0 && <div className="zone-editor-empty">{t('admin.zoneEmpty')}</div>}
                  {zones.map((z, i) => (
                    <div key={i} className="zone-editor-zone-row">
                      <input
                        type="text"
                        className="zone-editor-zone-name"
                        value={z.name}
                        onChange={(e) => renameZone(i, e.target.value)}
                      />
                      <span className="zone-editor-zone-coords">
                        x: {z.x_min}..{z.x_max}, y: {z.y_min}..{z.y_max}
                      </span>
                      <button className="admin-maps-btn danger" onClick={() => removeZone(i)}>
                        {t('admin.remove')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && <div className="zone-editor-error">{error}</div>}

            <div className="zone-editor-actions">
              <button className="admin-maps-btn" onClick={onClose} disabled={busy}>
                {t('matchDetail.cancel')}
              </button>
              {step === 'calibration' ? (
                <button
                  className="admin-maps-btn admin-maps-btn-primary"
                  onClick={saveCalibration}
                  disabled={busy}
                >
                  {t('admin.saveCalibration')}
                </button>
              ) : (
                <button
                  className="admin-maps-btn admin-maps-btn-primary"
                  onClick={saveZones}
                  disabled={busy}
                >
                  {t('admin.saveZones')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
